import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { GoogleReview } from '@app/@shared/model/google-reviews.model';
import { GoogleReviewsCommentsService } from '@app/@shared/services/api/google-reviews-comments.service';
import { NgbCarousel, NgbSlide } from '@ng-bootstrap/ng-bootstrap';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { CustomTooltipDirective } from '@app/@shared/directives/custom-tooltip.directive';
import { StarRatingComponent } from '../star-rating/star-rating.component';
import { isPlatformBrowser } from '@angular/common'; //RPTA 504
import { Inject, PLATFORM_ID } from '@angular/core'; //RPTA 504


@Component({
  selector: 'app-google-reviews-comments',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgbCarousel,
    NgbSlide,
    StarRatingComponent,
    CustomTooltipDirective,
  ],
  templateUrl: './google-reviews-comments.component.html',
  styleUrl: './google-reviews-comments.component.scss',
})
export class GoogleReviewsCommentsComponent implements OnInit, OnDestroy {
  cards: GoogleReview[] = [];
  isLoading!: boolean;
  maxLengthText: number = 180;
  intervalHours: number = 900000;
  currentLang: string = 'es';
  private intervalId?: number;

  /**
   * Devuelve un array de slides, cada uno con hasta 3 items, avanzando de a 1 item.
   */
  get slidingWindows(): GoogleReview[][] {
    const { cards } = this;
    const n = cards.length;
    if (n === 0) return [];
    if (n === 1) return [[cards[0]]];
    if (n === 2) return [[cards[0], cards[1]]];
    const result: GoogleReview[][] = [];
    for (let i = 0; i < n; i++) {
      // Siempre genera una ventana de 3, rellenando circularmente si hay menos de 3
      result.push([cards[i], cards[(i + 1) % n], cards[(i + 2) % n]]);
    }
    return result;
  }

  constructor(
    private googleService: GoogleReviewsCommentsService,
    public translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object //RPTA 504
  ) {
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLang = event.lang;
    });
  }
  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) { //RPTA 504
        // Primera carga: siempre ejecutar el servicio normalmente
        this.loadReviewsComments();

        // Configurar intervalo para verificar cada 15 minutos
        this.intervalId = window.setInterval(() => {
          this.checkSundayAt12();
        }, this.intervalHours);
    }    
  }

  ngOnDestroy(): void {
    // Limpiar el intervalo cuando el componente se destruye
    //if (this.intervalId) {
    if (isPlatformBrowser(this.platformId) && this.intervalId) {  
      clearInterval(this.intervalId);
    }
  }

  private checkSundayAt12(): void {
    const now = new Date();

    // Verificar si es domingo (0) y las 12:00
    if (!this.isSundayAt12(now)) {
      return;
    }

    // Verificar si ya se ejecutó en esta hora específica
    if (this.hasAlreadyExecutedToday(now)) {
      return;
    }

    // Ejecutar la carga de reseñas y marcar como ejecutado
    this.loadReviewsComments();
    this.markAsExecuted(now);
  }

  private isSundayAt12(date: Date): boolean {
    const SUNDAY = 0;
    const TARGET_HOUR = 12;

    return date.getDay() === SUNDAY && date.getHours() === TARGET_HOUR;
  }

  private hasAlreadyExecutedToday(currentDate: Date): boolean {
    
    if (!isPlatformBrowser(this.platformId)) return false; //RPTA 504

    const lastExecution = localStorage.getItem('hasGoogle');
   
    if (!lastExecution) {
      return false;
    }

    const targetTime = this.getSundayAt12Time(currentDate);
    const lastExecutionTime = new Date(lastExecution).getTime();

    return lastExecutionTime === targetTime.getTime();
  }

  private getSundayAt12Time(date: Date): Date {
    const sundayAt12 = new Date(date);
    sundayAt12.setHours(12, 0, 0, 0);
    return sundayAt12;
  }

  private markAsExecuted(date: Date): void {
    const executionTime = this.getSundayAt12Time(date);
    localStorage.setItem('hasGoogle', executionTime.toISOString());
  }

  private loadReviewsComments() {

    if (!isPlatformBrowser(this.platformId)) return; //REP 504

    this.isLoading = true;

    this.googleService.getReviewsComments().subscribe({
      next: (data) => {
        this.cards = data.filter((card) => card.rating >= 4);
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  truncateText(text: string, maxLength: number = this.maxLengthText): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  shouldShowTooltip(text: string): boolean {
    return text && text.length <= this.maxLengthText;
  }
}
